<script setup lang="ts">
import { computed, ref, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import type { Caregiver } from '@/api/caregivers';
import type { InstanceShifts, ShiftCalendarCell, ShiftsCalendar } from '@/api/shifts';
import { useDynamicHeight } from '@/composables/useDynamicHeight';
import { usePermissions } from '@/composables/usePermissions';
import { formatCurrency } from '@/utils/currency';
import {
  buildBlockRowCells, buildCellColumns, buildRenderRow, collectShiftsById, estimateRowHeight,
  ESTIMATED_ROW_HEIGHT, type CalendarRenderRow,
} from '@/utils/shifts-calendar-grid';
import type { PatientScheduleBlock } from '@/api/patient-schedule-blocks';
import { blocksByPatient, blockPeriodLabel, expandBlockToSlots } from '@/utils/schedule-blocks';

interface Props {
  // Raw calendar payload from the shallow query: its identity changes per
  // fetched payload, which is what drives the render-model computeds below.
  calendar?: ShiftsCalendar;
  viewGroupByClass?: 'patient' | 'caregiver';
  loading: boolean;
  // Whether the row menu offers Editar/Detener (a patient must be selected).
  showRowActions: boolean;
  // Selected patient whose schedule blocks get a sticky row (rows are caregivers then).
  blockRowPatientId?: number;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  shiftClick: [shiftId: number],
  createCell: [payload: { row: InstanceShifts, slotName: string | undefined, date: string }],
  createDate: [date: string],
  editShifts: [caregiver: Caregiver | null],
  stopShifts: [caregiver: Caregiver | null],
  createBlockCell: [payload: { slotName: string | undefined, date: string }],
  blockClick: [blockId: number],
}>();

const { t } = useI18n();
const { hasPermission } = usePermissions();

const today = new Date();

const canCreateShifts = computed(() => hasPermission.value('shifts', 'create'));
const canReadFinancialInfo = computed(() => hasPermission.value('shifts', 'read_financial_information'));

const calendarHeaders = computed(() => {
  const baseHeaders = [];
  if (props.viewGroupByClass === 'caregiver') {
    baseHeaders.push({ title: t('shiftsCalendar.caregiver'), value: 'caregiver.fullName', fixed: true });
  } else if (props.viewGroupByClass === 'patient') {
    baseHeaders.push({ title: t('shiftsCalendar.patient'), value: 'patient.fullName', fixed: true });
  }

  baseHeaders.push(...(props.calendar?.headers || []));

  return baseHeaders;
});

const cellColumns = computed(() => buildCellColumns(props.calendar?.headers || []));

// Render-ready plain rows, derived once per payload (see utils/shifts-calendar-grid).
const scheduleBlocks = computed(() => props.calendar?.scheduleBlocks ?? []);
const blocksByPatientId = computed(() => blocksByPatient(scheduleBlocks.value));
const blockById = computed(() => new Map(scheduleBlocks.value.map((block) => [block.id, block])));

function slotCellsFor(patientId: number | undefined) {
  if (!patientId) return [];
  const headers = props.calendar?.headers ?? [];

  return (blocksByPatientId.value.get(patientId) ?? [])
    .flatMap((block) => expandBlockToSlots(block, headers));
}

const renderRows = computed<CalendarRenderRow[]>(() => {
  const columns = cellColumns.value;
  const paintBlocks = props.viewGroupByClass === 'patient';

  return (props.calendar?.shifts || []).map((row) => buildRenderRow(
    row, columns, paintBlocks ? slotCellsFor(row.patient?.id) : [],
  ));
});

// Sticky "Bloqueos de horario" row for the selected patient (its rows are caregivers).
const blockRowCells = computed(() => {
  const cells = slotCellsFor(props.blockRowPatientId);
  if (!cells.length) return null;

  return buildBlockRowCells(cells, cellColumns.value);
});

// The virtual table needs a bounded viewport; cap it to the content height so
// small datasets keep looking like the previous auto-height table. Rows with
// stacked shifts in one cell grow beyond the base height, so estimate per row
// instead of assuming a uniform height (otherwise small datasets get a
// needless internal scrollbar).
const TABLE_HEIGHT_RATIO = 0.7;
// Vuetify's --v-table-header-height (default density): every thead row,
// including the block row, renders at this height.
const TABLE_HEADER_HEIGHT = 56;
// With zero rows the content height is just the sticky header, which then
// covers the whole box and hides the no-data/loading row below the fold.
// Reserve one row of space so the "No hay turnos asignados" state is visible.
const NO_DATA_ROW_HEIGHT = 57;
const { dynamicHeight } = useDynamicHeight(TABLE_HEIGHT_RATIO);
const BLOCK_ROW_HEIGHT = TABLE_HEADER_HEIGHT;
// Vuetify pins every header-slot row at top: 0; the block row sits under the first.
const blockRowStyle = { top: `${TABLE_HEADER_HEIGHT}px` };
const tableHeight = computed(() => {
  const headerHeight = TABLE_HEADER_HEIGHT + (blockRowCells.value ? BLOCK_ROW_HEIGHT : 0);
  const contentHeight = renderRows.value.length ?
    renderRows.value.reduce((total, row) => total + estimateRowHeight(row), headerHeight) :
    headerHeight + NO_DATA_ROW_HEIGHT;

  return Math.min(contentHeight, dynamicHeight.value);
});

// Tooltip content is resolved lazily for the one hovered shift.
const shiftById = computed(
  () => collectShiftsById(props.calendar?.shifts || [], cellColumns.value),
);

function scrollToToday() {
  nextTick(() => {
    const todayHeader = document.querySelector('th[data-is-today="true"]');
    if (todayHeader) {
      // block: 'nearest' only scrolls the table wrapper horizontally; 'center'
      // fights the virtual table's internal vertical scroll container.
      todayHeader.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'center' });
    }
  });
}

defineExpose({ scrollToToday });

// Re-center on today only when the visible range changes (month navigation or
// Vista switch) — not when a background revalidation refreshes cell contents,
// which would yank the user's horizontal scroll position.
const visibleRangeKey = computed(() => {
  const calendar = props.calendar;

  return calendar ? `${calendar.dateTitle}|${calendar.headers.length}` : undefined;
});

watch(visibleRangeKey, (value) => {
  if (value) {
    scrollToToday();
  }
}, { immediate: true });

// Shared overlay singletons: a per-shift v-tooltip (and per-row v-menu) means
// tens of thousands of Vuetify overlay instances at 10k+ shifts, which is what
// froze the monthly view. One instance of each is re-anchored to the hovered
// or clicked element through delegated listeners on the grid wrapper.
const shiftTooltipOpen = ref(false);
const shiftTooltipActivator = ref<HTMLElement>();
const hoveredShift = ref<ShiftCalendarCell>();

const careerTooltipOpen = ref(false);
const careerTooltipActivator = ref<HTMLElement>();
const hoveredCareerLabel = ref('');

const rowMenuOpen = ref(false);
const rowMenuActivator = ref<HTMLElement>();
const rowMenuCaregiver = ref<Caregiver | null>(null);

const blockTooltipOpen = ref(false);
const blockTooltipActivator = ref<HTMLElement>();
const hoveredBlock = ref<PatientScheduleBlock>();

function closeCalendarTooltips() {
  shiftTooltipOpen.value = false;
  careerTooltipOpen.value = false;
  blockTooltipOpen.value = false;
}

function showBlockTooltip(button: HTMLElement) {
  if (blockTooltipOpen.value && blockTooltipActivator.value === button) return;

  const block = blockById.value.get(Number(button.dataset.blockId));
  if (!block) return;

  closeCalendarTooltips();
  hoveredBlock.value = block;
  blockTooltipActivator.value = button;
  nextTick(() => {
    blockTooltipOpen.value = true;
  });
}

function calendarRowFromElement(element: HTMLElement) {
  const rowElement = element.closest<HTMLElement>('tr[data-row-idx]');
  if (!rowElement) return undefined;

  return props.calendar?.shifts[Number(rowElement.dataset.rowIdx)];
}

function showShiftTooltip(button: HTMLElement) {
  if (shiftTooltipOpen.value && shiftTooltipActivator.value === button) return;

  const shift = shiftById.value.get(Number(button.dataset.shiftId));
  if (!shift) return;

  closeCalendarTooltips();
  hoveredShift.value = shift;
  shiftTooltipActivator.value = button;
  nextTick(() => {
    shiftTooltipOpen.value = true;
  });
}

function showCareerTooltip(button: HTMLElement) {
  if (careerTooltipOpen.value && careerTooltipActivator.value === button) return;

  const caregiver = calendarRowFromElement(button)?.caregiver;
  if (!caregiver) return;

  closeCalendarTooltips();
  hoveredCareerLabel.value = caregiver.humanCareer || t('shiftsCalendar.withoutCareer');
  careerTooltipActivator.value = button;
  nextTick(() => {
    careerTooltipOpen.value = true;
  });
}

// Shared by mouse hover and keyboard focus so tooltips stay reachable when
// tabbing through the grid (the removed per-shift v-tooltip opened on focus).
const TOOLTIP_TARGETS: Array<[string, (button: HTMLElement) => void]> = [
  ['button[data-shift-id]', showShiftTooltip],
  ['button[data-row-menu]', showCareerTooltip],
  ['button[data-block-id]', showBlockTooltip],
];

function showTooltipsForTarget(target: HTMLElement) {
  for (const [selector, show] of TOOLTIP_TARGETS) {
    const button = target.closest<HTMLElement>(selector);
    if (button) {
      show(button);

      return;
    }
  }

  closeCalendarTooltips();
}

function onGridMouseover(event: MouseEvent) {
  showTooltipsForTarget(event.target as HTMLElement);
}

function onGridFocusin(event: FocusEvent) {
  showTooltipsForTarget(event.target as HTMLElement);
}

function openRowMenu(button: HTMLElement) {
  // Same activator: v-menu binds its own click handling to it, which already
  // toggled the menu — reopening here would make it impossible to close.
  if (rowMenuActivator.value === button) return;

  const caregiver = calendarRowFromElement(button)?.caregiver;
  if (!caregiver) return;

  closeCalendarTooltips();
  rowMenuCaregiver.value = caregiver;
  rowMenuActivator.value = button;
  nextTick(() => {
    rowMenuOpen.value = true;
  });
}

function emitCreateCell(button: HTMLElement) {
  if (!canCreateShifts.value) return;

  const cell = button.closest<HTMLElement>('td[data-slot]');
  const row = calendarRowFromElement(button);
  if (!cell || !row) return;

  emit('createCell', { row, slotName: cell.dataset.slot, date: cell.dataset.date ?? '' });
}

// Empty cells of the sticky block row create a block for that slot.
function emitCreateBlockCell(button: HTMLElement) {
  if (!canCreateShifts.value) return;

  const cell = button.closest<HTMLElement>('[data-slot]');
  if (!cell) return;

  emit('createBlockCell', { slotName: cell.dataset.slot, date: cell.dataset.date ?? '' });
}

function onGridClick(event: MouseEvent) {
  const target = event.target as HTMLElement;
  const shiftButton = target.closest<HTMLElement>('button[data-shift-id]');
  const nameButton = target.closest<HTMLElement>('button[data-row-menu]');
  const emptyCellButton = target.closest<HTMLElement>('button[data-empty-cell]');
  const blockButton = target.closest<HTMLElement>('button[data-block-id]');
  const blockEmptyButton = target.closest<HTMLElement>('button[data-block-empty]');

  if (shiftButton) emit('shiftClick', Number(shiftButton.dataset.shiftId));
  else if (nameButton) openRowMenu(nameButton);
  else if (emptyCellButton) emitCreateCell(emptyCellButton);
  else if (blockButton) emit('blockClick', Number(blockButton.dataset.blockId));
  else if (blockEmptyButton) emitCreateBlockCell(blockEmptyButton);
}
</script>
<template>
  <v-card class="w-100">
    <v-data-table-virtual
      :headers="calendarHeaders"
      :items="calendar?.shifts || []"
      :height="tableHeight"
      :item-height="ESTIMATED_ROW_HEIGHT"
      fixed-header
      :loading="loading"
      :loading-text="t('shiftsCalendar.loadingShifts')"
      :no-data-text="t('shiftsCalendar.noShifts')"
      @click="onGridClick"
      @mouseover="onGridMouseover"
      @mouseleave="closeCalendarTooltips"
      @focusin="onGridFocusin"
      @focusout="closeCalendarTooltips"
    >
      <template #top>
        <slot name="toolbar" />
      </template>
      <template #headers="{ headers: headersInfo }">
        <tr>
          <template
            v-for="(header, index) in headersInfo[0]"
            :key="header.title"
          >
            <th
              v-if="index === 0"
              colspan="1"
              class="font-weight-bold v-data-table-column--fixed v-data-table-column--last-fixed"
            >
              {{ header.title }}
            </th>
            <th
              v-else
              colspan="2"
              :class="header.headerProps?.class"
              :data-is-today="(header as any).date?.split('T')[0] === today.toISOString().split('T')[0]"
            >
              <v-tooltip
                v-if="header.headerProps?.holiday"
                activator="parent"
                location="top"
              >
                {{ header.headerProps?.holiday.title }}
              </v-tooltip>
              <v-btn
                v-if="canCreateShifts"
                variant="text"
                size="small"
                class="ma-0 pa-3 w-100 h-100 text-body-2 font-weight-bold"
                @click="emit('createDate', (header as any).date)"
              >
                {{ header.title }}
              </v-btn>
              <span
                v-else
                class="d-flex align-center justify-center w-100 h-100 pa-3 text-body-2 font-weight-bold"
              >
                {{ header.title }}
              </span>
            </th>
          </template>
        </tr>
        <tr
          v-if="blockRowCells"
          class="cal-block-row"
          data-testid="schedule-block-row"
        >
          <th
            class="font-weight-bold v-data-table-column--fixed v-data-table-column--last-fixed cal-block-row__th"
            :style="blockRowStyle"
          >
            <span class="d-flex align-center ga-1 text-body-2">
              <v-icon
                icon="mdi-calendar-lock"
                size="small"
              />
              {{ t('scheduleBlocks.rowTitle') }}
            </span>
          </th>
          <th
            v-for="cell in blockRowCells"
            :key="cell.name"
            class="cal-block-row__th"
            :class="cell.cellClass"
            :style="blockRowStyle"
            :data-slot="cell.name"
            :data-date="cell.date"
          >
            <div
              v-if="cell.blocks.length"
              class="d-flex flex-column w-100 h-100"
            >
              <button
                v-for="block in cell.blocks"
                :key="block.blockId"
                type="button"
                class="cal-block-btn"
                :data-block-id="block.blockId"
                :aria-label="block.description"
              >
                {{ t('scheduleBlocks.cellLetter') }}
              </button>
            </div>
            <button
              v-else
              type="button"
              class="cal-empty-btn"
              :disabled="!canCreateShifts"
              data-block-empty
            />
          </th>
        </tr>
      </template>
      <!-- internalItem.index is Vuetify's index into the RAW items array
           (assigned before sorting/filtering), so the data-row-idx lookup
           into calendar.shifts stays correct even if the table ever gains
           sorting. -->
      <template #item="{ internalItem, itemRef }">
        <tr
          :ref="itemRef"
          class="v-data-table__tr"
          :data-row-idx="internalItem.index"
        >
          <td class="v-data-table__td v-data-table-column--fixed v-data-table-column--last-fixed v-data-table-column--align-start">
            <button
              v-if="renderRows[internalItem.index]?.isCaregiverRow"
              type="button"
              class="cal-name-btn text-body-2"
              data-row-menu
            >
              <span
                class="cal-career-bar"
                :style="{ backgroundColor: renderRows[internalItem.index].careerColorHex }"
              />
              {{ renderRows[internalItem.index].name }}
            </button>
            <template v-else>
              {{ renderRows[internalItem.index]?.name }}
            </template>
          </td>
          <td
            v-for="cell in renderRows[internalItem.index]?.cells"
            :key="cell.name"
            class="v-data-table__td v-data-table-column--align-start"
            :class="[cell.cellClass, cell.blocks.length ? 'cal-cell--blocked' : '']"
            :data-slot="cell.name"
            :data-date="cell.date"
          >
            <div
              v-if="cell.shifts || cell.blocks.length"
              class="d-flex flex-column w-100 h-100"
            >
              <button
                v-for="shift in cell.shifts ?? []"
                :key="shift.id"
                type="button"
                class="cal-shift-btn"
                :data-shift-id="shift.id"
              >
                <span :class="shift.colorClass">{{ shift.letter }}</span>
                <span
                  v-if="shift.blocked"
                  class="mdi mdi-calendar-lock cal-shift-flag"
                  data-testid="shift-block-flag"
                />
              </button>
              <button
                v-for="block in cell.blocks"
                :key="`block-${block.blockId}`"
                type="button"
                class="cal-block-btn"
                :data-block-id="block.blockId"
                :aria-label="block.description"
              >
                {{ t('scheduleBlocks.cellLetter') }}
              </button>
            </div>
            <button
              v-else
              type="button"
              class="cal-empty-btn"
              :disabled="!canCreateShifts"
              data-empty-cell
            />
          </td>
        </tr>
      </template>
      <template #bottom>
        <div class="justify-end d-flex border-t-sm">
          <p class="mx-8 my-2 text-caption">
            <v-icon
              icon="mdi-information"
              class="mb-1"
            />
            {{ t('shiftsCalendar.legend.question') }}
            <v-tooltip
              activator="parent"
              location="bottom"
            >
              <p>{{ t('shiftsCalendar.legend.day') }}</p>
              <p>{{ t('shiftsCalendar.legend.night') }}</p>
              <p>{{ t('shiftsCalendar.legend.custom') }}</p>
              <p>{{ t('shiftsCalendar.legend.fullDay') }}</p>
              <p>{{ t('shiftsCalendar.legend.kinesiologist') }}</p>
              <p>{{ t('shiftsCalendar.legend.phonoaudiologist') }}</p>
            </v-tooltip>
          </p>
        </div>
      </template>
    </v-data-table-virtual>

    <!-- Shared singletons re-anchored via delegated grid events: one tooltip
         and one menu serve every shift/row instead of one instance each. -->
    <v-tooltip
      v-model="shiftTooltipOpen"
      :activator="shiftTooltipActivator"
      :open-on-hover="false"
      location="top"
    >
      <template v-if="hoveredShift">
        <div>{{ t('shiftsCalendar.shiftTooltip.schedule', { time: hoveredShift.humanTime }) }}</div>
        <div
          v-if="hoveredShift.scheduleBlock"
          class="font-weight-bold"
        >
          {{ t('scheduleBlocks.shiftFlagTooltip', { description: hoveredShift.scheduleBlock.description }) }}
        </div>
        <template v-if="canReadFinancialInfo">
          <div>{{ t('shiftsCalendar.shiftTooltip.price', { price: formatCurrency(hoveredShift.price ?? 0) }) }}</div>
          <div v-if="hoveredShift.caregiverContractedWithSalary">
            {{ t('shiftsCalendar.shiftTooltip.salaried') }}
          </div>
          <template v-else>
            <div>
              {{ t('shiftsCalendar.shiftTooltip.caregiverCost',
                   { cost: formatCurrency(hoveredShift.caregiverCost ?? 0) }) }}
            </div>
            <div>{{ t('shiftsCalendar.shiftTooltip.taxBill', { taxBill: hoveredShift.humanTaxBill }) }}</div>
          </template>
        </template>
      </template>
    </v-tooltip>
    <v-tooltip
      v-model="careerTooltipOpen"
      :activator="careerTooltipActivator"
      :open-on-hover="false"
      location="top"
    >
      {{ hoveredCareerLabel }}
    </v-tooltip>
    <v-tooltip
      v-model="blockTooltipOpen"
      :activator="blockTooltipActivator"
      :open-on-hover="false"
      location="top"
    >
      <template v-if="hoveredBlock">
        <div class="font-weight-bold">
          {{ hoveredBlock.description }}
        </div>
        <div>{{ blockPeriodLabel(hoveredBlock) }}</div>
        <div>{{ t('scheduleBlocks.blockTooltip.careers', { careers: hoveredBlock.humanCareers }) }}</div>
        <div
          v-if="canCreateShifts"
          class="text-caption"
        >
          {{ t('scheduleBlocks.blockTooltip.edit') }}
        </div>
      </template>
    </v-tooltip>
    <v-menu
      v-model="rowMenuOpen"
      :activator="rowMenuActivator"
      location="end"
      transition="slide-x-transition"
      offset="18 2"
    >
      <v-list
        class="pa-0"
        lines="two"
        slim
      >
        <v-list-item
          v-if="rowMenuCaregiver?.id"
          :href="`/caregivers/${rowMenuCaregiver.id}`"
        >
          <template #prepend>
            <v-icon
              size="large"
              color="primary"
              style="opacity: 1;"
            >
              mdi-account-outline
            </v-icon>
          </template>
          <v-list-item-title class="text-primary font-weight-bold">
            {{ t('shiftsCalendar.rowMenu.viewProfile') }}
          </v-list-item-title>
          <v-list-item-subtitle>
            {{ t('shiftsCalendar.rowMenu.viewProfileSubtitle') }}
          </v-list-item-subtitle>
        </v-list-item>
        <v-divider />
        <v-list-item
          v-if="showRowActions"
          @click="emit('editShifts', rowMenuCaregiver)"
        >
          <template #prepend>
            <v-icon
              size="large"
              color="secondary"
              style="opacity: 1;"
            >
              mdi-calendar-outline
            </v-icon>
          </template>
          <v-list-item-title class="text-secondary font-weight-bold">
            {{ t('shiftsCalendar.rowMenu.editShifts') }}
          </v-list-item-title>
          <v-list-item-subtitle>
            {{ t('shiftsCalendar.rowMenu.editShiftsSubtitle') }}
          </v-list-item-subtitle>
        </v-list-item>
        <v-divider />
        <v-list-item
          v-if="showRowActions && rowMenuCaregiver"
          @click="emit('stopShifts', rowMenuCaregiver)"
        >
          <template #prepend>
            <v-icon
              size="large"
              color="red"
              style="opacity: 1;"
            >
              mdi-close-circle-outline
            </v-icon>
          </template>
          <v-list-item-title class="text-red font-weight-bold">
            {{ t('shiftsCalendar.rowMenu.stopShifts') }}
          </v-list-item-title>
          <v-list-item-subtitle>
            {{ t('shiftsCalendar.rowMenu.stopShiftsSubtitle') }}
          </v-list-item-subtitle>
        </v-list-item>
      </v-list>
    </v-menu>
  </v-card>
</template>
<style scoped>
/* Plain-HTML calendar cells: visually equivalent to the previous per-shift
   v-btn (variant=text, size=small) without mounting a Vuetify component (plus
   an overlay tooltip) per shift — tens of thousands of instances froze the
   monthly view at 10k+ shifts. */

.cal-shift-btn,
.cal-empty-btn,
.cal-name-btn {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0;
  background: transparent;
  border: 0;
  border-radius: 4px;
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
  cursor: pointer;
}

.cal-shift-btn,
.cal-empty-btn {
  justify-content: center;
  /* the previous v-btn size=small enforced this; without it empty day/night
     subcolumns collapse and column widths drift */
  min-width: 50px;
  font-size: .75rem;
  font-weight: 500;
  letter-spacing: .0892857143em;
  text-transform: uppercase;
}

.cal-shift-btn {
  flex: 1 1 auto;
  min-height: 0;
}

.cal-empty-btn {
  height: 100%;
}

.cal-empty-btn:disabled {
  cursor: default;
}

.cal-name-btn {
  justify-content: flex-start;
  height: 100%;
  padding-right: 8px;
  font-weight: 500;
  text-align: left;
  text-transform: none;
  white-space: nowrap;
}

.cal-shift-btn:hover,
.cal-name-btn:hover,
.cal-empty-btn:not(:disabled):hover {
  background: rgba(var(--v-theme-on-surface), .04);
}

.cal-shift-btn:focus-visible,
.cal-name-btn:focus-visible,
.cal-empty-btn:not(:disabled):focus-visible {
  background: rgba(var(--v-theme-on-surface), .12);
  outline: none;
}

/* Mirrors the previous vertical v-divider career bar (thickness 3, 32px,
   border-opacity-75). */

.cal-career-bar {
  flex: 0 0 auto;
  width: 3px;
  height: 32px;
  margin: 8px 8px 8px 0;
  opacity: .75;
}

/* Patient schedule blocks: hatched grey "turno no turno" cells showing just
   the "B" letter; description, period and careers live in the tooltip. */
.cal-block-btn {
  display: block;
  flex: 1 1 auto;
  width: 100%;
  min-width: 50px;
  min-height: 24px;
  padding: 2px 4px;
  border: 0;
  border-radius: 4px;
  background-image: repeating-linear-gradient(45deg,
  rgba(96, 96, 96, .12) 0,
  rgba(96, 96, 96, .12) 6px,
  rgba(96, 96, 96, .22) 6px,
  rgba(96, 96, 96, .22) 12px);
  color: rgba(0, 0, 0, .7);
  font-size: .75rem;
  font-weight: 600;
  line-height: 1.2;
  text-align: center;
  text-transform: none;
  cursor: pointer;
}

.cal-block-btn:hover,
.cal-block-btn:focus-visible {
  color: rgba(0, 0, 0, .9);
  outline: 1px solid rgba(96, 96, 96, .8);
  outline-offset: -1px;
}

.cal-cell--blocked {
  background-image: repeating-linear-gradient(45deg,
  rgba(96, 96, 96, .05) 0,
  rgba(96, 96, 96, .05) 6px,
  rgba(96, 96, 96, .1) 6px,
  rgba(96, 96, 96, .1) 12px);
}

.cal-shift-flag {
  margin-left: 2px;
  font-size: 12px;
  opacity: .7;
}

.cal-block-row__th {
  font-weight: 400;
  vertical-align: middle;
  background: rgb(var(--v-theme-surface));
}
</style>
